/**
 * صفحة «الإصدارات الجديدة» — /new-product
 * إن كانت هناك صفحة منتج واحدة فقط تُعرض بلوكاتها مباشرةً،
 * وإن كانت أكثر تُعرض قائمة بالمنتجات كلٌّ باسمه (وكلّ واحد له /new-product/<slug>).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { DICTS } from "@/lib/i18n";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { NewProductBlocks } from "@/components/newproduct/NewProductBlocks";
import { AutoMedia } from "@/components/AutoMedia";
import { softCtaClass, SoftCtaArrow } from "@/components/ui/soft-cta";
import { getNewProductPages } from "@/utils/newProductBlocks.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/new-product/")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["newproduct.meta.title"] },
      { name: "description", content: DICTS.en["newproduct.meta.description"] },
      { property: "og:title", content: DICTS.en["newproduct.meta.title"] },
      { property: "og:description", content: DICTS.en["newproduct.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/new-product` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/new-product` }],
  }),
  component: NewProductIndexPage,
});

function NewProductIndexPage() {
  const { locale, t } = useLocale();
  const fn = useServerFn(getNewProductPages);
  const { data, isLoading } = useQuery({
    queryKey: ["new-product-pages"],
    queryFn: () => fn(),
    staleTime: 60_000,
  });
  const pages = data?.pages ?? [];

  if (isLoading) {
    return (
      <SiteShell>
        <div className="min-h-[60svh]" />
      </SiteShell>
    );
  }

  if (pages.length === 1) {
    return (
      <SiteShell>
        <NewProductBlocks slug={pages[0].slug} />
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-[clamp(48px,8vw,110px)]">
        <h1 className="font-serif text-[clamp(28px,5vw,54px)] leading-tight">
          {t("newproduct.index.title")}
        </h1>
        <p className="mt-3 font-sans text-sm text-muted-foreground">
          {t("newproduct.index.subtitle")}
        </p>

        {pages.length === 0 ? (
          <p className="mt-10 font-sans text-sm text-muted-foreground">
            {t("newproduct.index.empty")}
          </p>
        ) : (
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {pages.map((p) => {
              const name = (locale === "ar" ? p.nameAr : p.nameEn) || p.slug;
              return (
                <Link
                  key={p.id}
                  to="/new-product/$slug"
                  params={{ slug: p.slug }}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
                    {p.coverUrl ? (
                      <AutoMedia
                        src={p.coverUrl}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        manualPlay
                      />
                    ) : null}
                  </div>
                  <h2 className="mt-4 font-serif text-xl">{name}</h2>
                  <span className={`${softCtaClass} mt-2`}>
                    {t("newproduct.index.view")}
                    <SoftCtaArrow />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </SiteShell>
  );
}
